import { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import * as z from "zod/v4";
import Layout from "../layout/Layout";
import useWindowWidthThreshold from "../hooks/useWindowWidthThreshold";
import { usePreorderState } from "../hooks/usePreorderState";
import { useMultiPersonOrdering } from "../hooks/useMultiPersonOrdering";
import Basket from "./basket";
import BasketFooter from "./basketFooter";
import type { CustomerPreOrder, PreOrderItem, Booking, DepositRequired } from "../../../common/jsonRpcTypes";
import "../../styles/pre-orders.scss";
import "../../styles/multi-person-ordering.scss";
import { InfoCircle } from "../icons";
import AlertBanner from "../shared/AlertBanner";
import PageStateGuard from "../shared/PageStateGuard";
import { isMultiPersonOrderingEnabled, resolveExcessNameValidity, type MenuLimits } from "./preOrderUtils";
import { PreorderProvider, type PreorderContextValue, type LinkedBooking } from "../context/PreorderContext";
import OrderSummary from "../orderSummary";
import PrimaryButton from "../buttons/primaryButton";
import { PreorderContent } from "./PreorderContent";
import { PreorderExpiryWarning } from "./PreorderExpiryWarning";
import { useWidgetRequests } from "../context/WidgetRequestContext";
import { buildBookingTypeGroups, type BookingTypeGroup } from "../../utils/bookingTypeGroups";
import { isAnyOrderedMenuPaidByGroupMember } from "../../utils/preorderSettingsUtils";
import { translatePreorderRefusal } from "../../utils/preorderRefusalMessage";

/** Stable empty list, so hiding multi-person ordering does not churn the context value. */
const NO_NAMES: string[] = [];

/** Result passed to the onComplete callback when the user finishes preordering. */
export interface PreorderCompleteResult {
  items: PreOrderItem[];
  totalCost: number;
  serviceChargePercentage: number;
  serviceChargeAmount: number;
  totalCostWithService: number;
  shouldHidePrices: boolean;
  hidePricesByMenu: Record<string, boolean>;
  pendingPreorderId: string | undefined;
  /** Whether the preorder is tax-inclusive (false = tax-exclusive). */
  taxInclusive?: boolean;
  /** Tax label from API (e.g. "VAT", "Sales Tax"). */
  taxLabel?: string;
  /** Gross total including tax (for tax-exclusive venues). */
  totalGross?: number;
  /** Tax breakdown entries (for tax-exclusive venues). */
  taxBreakdown?: { label: string; amount: number }[];
  /** Required payment amount based on payment percentage (from backend). */
  requiredPaymentAmount?: number;
  /** Required payment amount including tax for tax-exclusive venues (from backend). */
  requiredPaymentGross?: number;
  /** Per-booking-type breakdown, populated only for linked bookings. */
  bookingTypeGroups?: BookingTypeGroup[];
  /**
   * Dietary requirements captured in the dietary modal. Their shared order note
   * is stored mirrored on every entry and is what the /book submit sends as
   * `customer_notes`.
   */
  dietaryRequirements?: CustomerPreOrder["dietaryRequirements"];
}

export interface PreorderExperienceProps {
  /** Booking ID used for API calls. */
  readonly bookingId: string;
  /** Optional order ID when editing an existing order. */
  readonly orderId?: string;
  /** Pre-loaded booking data in the Booking shape. */
  readonly bookingData: Booking;
  /** Called when the user completes preorder selection (used by /book flow). */
  readonly onComplete?: (result: PreorderCompleteResult) => void | Promise<void>;
  /** Called when the user presses the back button. */
  readonly onBack?: () => void;
  readonly onCacheExpired?: () => void;
  readonly onCacheExtended?: () => void;
  /** Whether to show the header with breadcrumbs (true for /place-your-order, false for /book). */
  readonly showHeader: boolean;
  /** Pre-loaded CustomerPreOrder data — skips the /getCustomerPreOrder API call when provided. */
  readonly initialCustomerPreOrder?: CustomerPreOrder;
  /** Default service charge percentage from booking options. */
  readonly defaultServiceChargePercentage?: number;
  /** Linked booking types with their preorder menus. */
  readonly linkedBookings?: LinkedBooking[] | null;
  /** Booking type name for the main booking. */
  readonly bookingTypeName?: string;
  /** Booking type ID for the main booking. */
  readonly bookingTypeId?: string;
  /** Deposit information - when present with a preorder, allows proceeding with empty basket */
  readonly depositRequired?: import("../../../common/jsonRpcTypes").DepositRequired;
  /** Whether the current order is valid (passes minimum spend, item count, etc.). */
  readonly isOrderValid?: boolean;
  /** Preorder menus for minimum spend validation (used in /book flow). */
  readonly preorderMenus?: import("../../../common/jsonRpcTypes").PreorderMenu[];
  /**
   * Whether the venue has disabled the shareable pre-order link for the selected
   * booking type. Supplied by the /book flow, where the pending booking carries
   * no `customer_preorder_settings` of its own. When omitted the setting is read
   * from the loaded pre-order data instead.
   */
  readonly disableShareableLink?: boolean;
  /** Widget heading slot the pre-order title portals into, as on the payment stages. */
  readonly headingSlot?: HTMLElement | null;
  /**
   * Hides the "Place your order" CTA — set when the booking's outcome is
   * already unknown (a timed-out `/submitBooking`).
   */
  readonly submitHidden?: boolean;
}

interface AsideContentProps {
  readonly bookingId: string;
  readonly customerPreOrder: CustomerPreOrder | undefined;
  readonly setForNames: React.Dispatch<React.SetStateAction<string[]>>;
  readonly handleSetOrderingFor: (index: number) => void;
  readonly handleRemoveName: (index: number) => Promise<void>;
  readonly handleNamesChanged: (names: string[]) => Promise<void>;
  readonly showServiceChargeSelector?: boolean;
  readonly onServiceChargeChange?: (percentage: number) => Promise<boolean>;
  readonly showServiceChargeInBasket?: boolean;
  readonly onBasketServiceChargeChange?: (percentage: number) => Promise<boolean>;
  readonly canChangeServiceCharge?: boolean;
  readonly showConfirmDetails: boolean;
  readonly goToNextStep: () => void;
  readonly isOrderValid: boolean;
  readonly isSubmitting: boolean;
  readonly requiredPaymentAmount?: number;
  readonly menuLimits?: MenuLimits;
  readonly onMaxItemsError?: (menuId: string, errorMessage: string) => void;
  readonly shouldHidePrices: boolean;
  readonly bookingData: PreorderContextValue["bookingData"];
  readonly aggregatedTotals: {
    totalCost: number;
    totalCostWithService: number;
    serviceChargeAmount: number;
    serviceChargePercentage: number;
    itemsCount: number;
    items: PreOrderItem[];
  };
  readonly depositRequired?: DepositRequired;
  readonly onCacheExpired: () => void | Promise<void>;
  readonly isCacheExpired: boolean;
  readonly resyncKey?: number;
  readonly submitHidden?: boolean;
}

function AsideContent(props: AsideContentProps): React.ReactNode {
  const { customerPreOrder, showConfirmDetails, aggregatedTotals } = props;
  let mainContent: React.ReactNode = null;
  let footerContent: React.ReactNode = null;

  if (showConfirmDetails) {
    mainContent = (
      <OrderSummary
        preorder={customerPreOrder}
        shouldHidePrices={props.shouldHidePrices}
        canChangeServiceCharge={props.showServiceChargeSelector && props.canChangeServiceCharge}
        onServiceChargeChange={props.onServiceChargeChange}
        requiredPaymentAmount={props.requiredPaymentAmount}
      />
    );
  } else if (customerPreOrder) {
    mainContent = (
      <Basket
        bookingId={props.bookingId}
        setForNames={props.setForNames}
        onSetOrderingFor={props.handleSetOrderingFor}
        onRemoveName={props.handleRemoveName}
        onNamesChanged={props.handleNamesChanged}
        menuLimits={props.menuLimits}
        onMaxItemsError={props.onMaxItemsError}
        totalCost={aggregatedTotals.totalCost}
        totalCostWithService={aggregatedTotals.totalCostWithService}
        serviceChargeAmount={aggregatedTotals.serviceChargeAmount}
        serviceChargePercentage={aggregatedTotals.serviceChargePercentage}
        showServiceChargeSelector={props.showServiceChargeInBasket}
        canChangeServiceCharge={props.canChangeServiceCharge}
        onServiceChargeChange={props.onBasketServiceChargeChange}
        isCacheExpired={props.isCacheExpired}
      />
    );
  }

  if ((customerPreOrder || props.depositRequired) && !showConfirmDetails) {
    footerContent = (
      <BasketFooter
        items={aggregatedTotals.items}
        totalCost={aggregatedTotals.totalCost}
        totalCostWithService={aggregatedTotals.totalCostWithService}
        goToNextStep={props.goToNextStep}
        itemsCount={aggregatedTotals.itemsCount}
        isOrderValid={props.isOrderValid}
        isSubmitting={props.isSubmitting}
        serviceChargeAmount={aggregatedTotals.serviceChargeAmount}
        serviceChargePercentage={aggregatedTotals.serviceChargePercentage}
        hideServiceCharge={props.showServiceChargeInBasket}
        depositRequired={props.depositRequired}
        customerPreOrder={customerPreOrder}
        submitHidden={props.submitHidden}
      />
    );
  }

  if (!mainContent && !footerContent) {
    return null;
  }

  return (
    <>
      {mainContent}
      <PreorderExpiryWarning
        bookingId={props.bookingId}
        active={!!customerPreOrder}
        onExpired={props.onCacheExpired}
        resyncKey={props.resyncKey}
      />
      {footerContent}
    </>
  );
}

/** Resolve the display name of a booking type from the basket, the linked bookings or the main booking type. */
function resolveBookingTypeName(
  bookingTypeId: string,
  preOrder: CustomerPreOrder | undefined,
  linkedBookings: LinkedBooking[] | null,
  mainBookingTypeId: string | undefined,
  mainBookingTypeName: string | undefined,
): string | undefined {
  if (preOrder?.bookingTypeName) return preOrder.bookingTypeName;
  const linkedBooking = linkedBookings?.find((booking) => booking.bookingTypeId === bookingTypeId);
  if (linkedBooking) return linkedBooking.bookingTypeName;
  return bookingTypeId === mainBookingTypeId ? mainBookingTypeName : undefined;
}

function hasServiceCharge(percentage: number | null): boolean {
  return percentage !== null && percentage > 0;
}

/**
 * Resolves the multi-person ordering state the context is built from.
 *
 * Venues can switch off the shareable pre-order link, which hides the
 * multi-person controls. Names restored from a previous session (local
 * storage) are ignored while the setting is on, so the guest is never left
 * with a per-person basket they have no way to turn off.
 */
function resolveMultiPersonOrdering(
  customerPreOrder: CustomerPreOrder | undefined,
  disableShareableLink: boolean | undefined,
  forNames: string[],
  multipleNames: boolean,
): { enabled: boolean; forNames: string[]; multipleNames: boolean } {
  const enabled = isMultiPersonOrderingEnabled(customerPreOrder, disableShareableLink);
  return {
    enabled,
    forNames: enabled ? forNames : NO_NAMES,
    multipleNames: enabled && multipleNames,
  };
}

/**
 * Merges the parent's verdict on the order with the basket's own.
 *
 * The /place-your-order flow supplies its own validation, which must agree
 * with the basket's rules; the /book flow supplies none, leaving the basket's
 * rules to stand alone. Extracted from the component to keep its cognitive
 * complexity within the Sonar threshold.
 */
function resolveBasketValidity(parentIsOrderValid: boolean | undefined, internalIsOrderValid: boolean): boolean {
  return parentIsOrderValid !== undefined ? parentIsOrderValid && internalIsOrderValid : internalIsOrderValid;
}

function shouldShowServiceChargeInBasketHelper(
  onComplete: ((result: PreorderCompleteResult) => void | Promise<void>) | undefined,
  customerPreOrder: CustomerPreOrder | undefined,
  needsServiceChargeConfirm: boolean,
  hasLinkedBookings: boolean,
): boolean {
  return (
    !!onComplete &&
    !!customerPreOrder &&
    (customerPreOrder.items?.length ?? 0) > 0 &&
    needsServiceChargeConfirm &&
    !hasLinkedBookings
  );
}

function shouldShowServiceChargeSelectorHelper(
  onComplete: ((result: PreorderCompleteResult) => void | Promise<void>) | undefined,
  showConfirmDetails: boolean,
  customerPreOrder: CustomerPreOrder | undefined,
  needsServiceChargeConfirm: boolean,
  hasLinkedBookings: boolean,
): boolean {
  return (
    !onComplete &&
    showConfirmDetails &&
    !!customerPreOrder &&
    (customerPreOrder.items?.length ?? 0) > 0 &&
    needsServiceChargeConfirm &&
    !hasLinkedBookings
  );
}

/**
 * PreorderExperience — full-featured preorder UI used by both /place-your-order
 * and /book routes.
 *
 * Renders menus, basket, multi-person ordering, dietary requirements, and
 * service charge selection. The component delegates payment to the parent
 * via the `onComplete` callback when `showInlinePayment` is false.
 */
const PreorderExperience: React.FC<PreorderExperienceProps> = ({
  bookingId,
  orderId,
  bookingData,
  onComplete,
  onBack,
  onCacheExpired,
  onCacheExtended,
  showHeader,
  initialCustomerPreOrder,
  defaultServiceChargePercentage: _defaultServiceChargePercentage,
  linkedBookings = null,
  bookingTypeName,
  bookingTypeId,
  depositRequired: depositRequiredFromProps,
  isOrderValid: isOrderValidProp,
  preorderMenus = [],
  disableShareableLink,
  headingSlot,
  submitHidden = false,
}) => {
  const isDesktop = useWindowWidthThreshold(1024);
  const [state, setState] = useState<{ stage: "preorders" | "confirm" }>({ stage: "preorders" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A basket change still in flight must settle before the guest moves on.
  const isScreenBusy = useWidgetRequests()?.isScreenBusy ?? false;
  const [isCacheExpired, setIsCacheExpired] = useState(false);
  const [resyncKey, setResyncKey] = useState(0);
  const { t } = useTranslation();

  const [forNames, setForNames] = useState<string[]>([]);
  const [multipleNames, setMultipleNames] = useState(false);
  const [editNames, setEditNames] = useState(false);

  // Portal target: when rendered inside the /book flow (showHeader=false),
  // the basket aside is portaled into the outer BookingForm Layout's aside
  // so it appears beside the booking info sidebar.
  const [basketPortalTarget, setBasketPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!showHeader) {
      const el = document.getElementById("book-basket-portal");
      if (el) setBasketPortalTarget(el);
    }
  }, [showHeader]);

  const localPreOrderSchema = useMemo(() => z.object({ forNames: z.array(z.string()) }), []);

  const handleLocalPreOrderLoaded = useCallback(
    (localPreOrder: unknown) => {
      const parsed = localPreOrderSchema.safeParse(localPreOrder);
      if (!parsed.success) return;
      const names = parsed.data.forNames;
      const hasNames = names.length > 0;
      setForNames(names);
      setMultipleNames(hasNames);
      setEditNames(hasNames);
    },
    [localPreOrderSchema],
  );

  const {
    customerPreOrder,
    customerPreOrders,
    setCustomerPreOrder,
    setCustomerPreOrders,
    isLoadingPreorderData,
    isPreorderUnavailable,
    preorderUnavailableMessage,
    preorderLimitMessage,
    menuLimits,
    setMenuLimits,
    externalValidationErrors,
    originalServiceChargePercentage,
    updateCustomerPreOrder,
    isItemPending,
    refreshCustomerPreOrder,
    reloadServerBasket,
    handleServiceChargeChange,
    handleValidationChange,
    handleMaxItemsError,
    isOrderValid: isOrderValidInternal,
    hidePricesByMenu,
    shouldHidePrices,
  } = usePreorderState({
    bookingId,
    orderId,
    bookingData,
    forNames,
    onLocalPreOrderLoaded: handleLocalPreOrderLoaded,
    initialCustomerPreOrder,
    preorderMenus,
    depositRequired: depositRequiredFromProps,
  });

  const handleCacheExpired = useCallback(() => {
    setIsCacheExpired(true);
    setCustomerPreOrders({});
    setState({ stage: "preorders" });
    onCacheExpired?.();
    return refreshCustomerPreOrder(false);
  }, [onCacheExpired, refreshCustomerPreOrder, setCustomerPreOrders]);

  // Clears the cache-expired banner and resyncs the expiry warning after a successful basket write.
  const updateCustomerPreOrderAndClearExpiry = useCallback(
    async (request: Parameters<typeof updateCustomerPreOrder>[0]): Promise<boolean> => {
      const ok = await updateCustomerPreOrder(request);
      if (ok) {
        setResyncKey((key) => key + 1);
        setIsCacheExpired(false);
        onCacheExtended?.();
      }
      return ok;
    },
    [onCacheExtended, updateCustomerPreOrder],
  );

  const {
    enabled: multiPersonOrderingEnabled,
    forNames: effectiveForNames,
    multipleNames: effectiveMultipleNames,
  } = resolveMultiPersonOrdering(customerPreOrder, disableShareableLink, forNames, multipleNames);

  const {
    orderingFor,
    addName,
    setAddName,
    addNameError,
    namesExceedGuests,
    handleMultipleNamesOptionChange,
    handleAddName,
    handleRemoveName,
    handleSetOrderingFor,
    syncDietaryRequirementNames,
  } = useMultiPersonOrdering({
    bookingId,
    bookingType: bookingTypeId ?? "default",
    customerPreOrder,
    setCustomerPreOrder,
    updateCustomerPreOrder: updateCustomerPreOrderAndClearExpiry,
    refreshCustomerPreOrder,
    reloadServerBasket,
    forNames,
    setForNames,
    multipleNames,
    setMultipleNames,
    editNames,
    setEditNames,
    guestCount: bookingData?.guestCount,
  });

  const { isOrderValid, showNamesExceedGuests } = resolveExcessNameValidity(
    resolveBasketValidity(isOrderValidProp, isOrderValidInternal),
    namesExceedGuests,
    multiPersonOrderingEnabled,
  );

  // Calculate aggregated totals when multiple booking types are present
  const aggregatedTotals = useMemo(() => {
    const hasMultipleBookingTypes = customerPreOrders && Object.keys(customerPreOrders).length > 1;

    if (!hasMultipleBookingTypes) {
      return {
        totalCost: customerPreOrder?.totalCost ?? 0,
        totalCostWithService: customerPreOrder?.totalCostWithService ?? 0,
        serviceChargeAmount: customerPreOrder?.serviceChargeAmount ?? 0,
        serviceChargePercentage: customerPreOrder?.serviceChargePercentage ?? 0,
        itemsCount: customerPreOrder?.items?.length ?? 0,
        items: customerPreOrder?.items ?? [],
      };
    }

    let aggregatedCost = 0;
    let aggregatedCostWithService = 0;
    let aggregatedServiceCharge = 0;
    let aggregatedServiceChargePercentage = customerPreOrder?.serviceChargePercentage ?? 0;
    let aggregatedItemsCount = 0;
    let aggregatedItems: PreOrderItem[] = [];

    for (const preOrder of Object.values(customerPreOrders)) {
      aggregatedCost += preOrder?.totalCost ?? 0;
      aggregatedCostWithService += preOrder?.totalCostWithService ?? 0;
      aggregatedServiceCharge += (preOrder?.totalCostWithService ?? 0) - (preOrder?.totalCost ?? 0);
      aggregatedItemsCount += preOrder?.items?.length ?? 0;
      if (preOrder?.items) {
        aggregatedItems = [...aggregatedItems, ...preOrder.items];
      }
      // Use the service charge percentage from the first booking type
      if (preOrder?.serviceChargePercentage !== undefined) {
        aggregatedServiceChargePercentage = preOrder.serviceChargePercentage;
      }
    }

    return {
      totalCost: aggregatedCost,
      totalCostWithService: aggregatedCostWithService,
      serviceChargeAmount: aggregatedServiceCharge,
      serviceChargePercentage: aggregatedServiceChargePercentage,
      itemsCount: aggregatedItemsCount,
      items: aggregatedItems,
    };
  }, [customerPreOrders, customerPreOrder]);

  // One entry per booking type of a linked booking, so the payment step and the
  // confirmation page can show what was ordered against each sitting.
  const bookingTypeGroups = useMemo(
    () =>
      buildBookingTypeGroups(
        Object.entries(customerPreOrders ?? {}).map(([id, preOrder]) => ({
          bookingType: id,
          bookingTypeName: resolveBookingTypeName(id, preOrder, linkedBookings, bookingTypeId, bookingTypeName),
          items: preOrder?.items,
          forNames: preOrder?.forNames,
          totalCost: preOrder?.totalCost,
          totalCostWithService: preOrder?.totalCostWithService,
          serviceChargeAmount: preOrder?.serviceChargeAmount,
          booking: preOrder?.booking,
        })),
        {
          mainBookingTypeId: bookingTypeId,
          mainBookingTypeName: bookingTypeName,
          mainBookingTime: bookingData?.time,
        },
      ),
    [customerPreOrders, linkedBookings, bookingTypeId, bookingTypeName, bookingData?.time],
  );

  const buildCompleteResult = useCallback((): PreorderCompleteResult | null => {
    // Allow proceeding when either customerPreOrder exists OR depositRequired exists
    if (!customerPreOrder && !depositRequiredFromProps) return null;

    // When no preorder but deposit exists, create a minimal result for deposit-only flow
    if (!customerPreOrder && depositRequiredFromProps) {
      return {
        items: [],
        totalCost: 0,
        serviceChargePercentage: 0,
        serviceChargeAmount: 0,
        totalCostWithService: 0,
        shouldHidePrices,
        hidePricesByMenu,
        pendingPreorderId: undefined,
        taxInclusive: depositRequiredFromProps.tax_inclusive,
        taxLabel: depositRequiredFromProps.tax_label,
        totalGross: depositRequiredFromProps.gross_amount,
        taxBreakdown: depositRequiredFromProps.formatted_tax_rates?.map((rate: any) => ({
          label: rate.label,
          amount: rate.tax,
        })),
        requiredPaymentAmount: depositRequiredFromProps.total,
        requiredPaymentGross: depositRequiredFromProps.gross_amount ?? depositRequiredFromProps.total,
      };
    }

    // Normal preorder flow
    return {
      items: aggregatedTotals.items,
      totalCost: aggregatedTotals.totalCost,
      serviceChargePercentage: aggregatedTotals.serviceChargePercentage,
      serviceChargeAmount: aggregatedTotals.serviceChargeAmount,
      totalCostWithService: aggregatedTotals.totalCostWithService,
      shouldHidePrices,
      hidePricesByMenu,
      pendingPreorderId: customerPreOrder?.pendingPreorderId,
      taxInclusive: customerPreOrder?.tax_inclusive,
      taxLabel: customerPreOrder?.tax_label,
      totalGross: customerPreOrder?.total_gross,
      taxBreakdown: customerPreOrder?.tax_breakdown,
      requiredPaymentAmount: customerPreOrder?.requiredPaymentAmount,
      requiredPaymentGross: customerPreOrder?.requiredPaymentGross,
      bookingTypeGroups,
      dietaryRequirements: customerPreOrder?.dietaryRequirements,
    };
  }, [
    customerPreOrder,
    depositRequiredFromProps,
    shouldHidePrices,
    hidePricesByMenu,
    aggregatedTotals,
    bookingTypeGroups,
  ]);

  const needsServiceChargeConfirm = hasServiceCharge(originalServiceChargePercentage);

  const goToNextStep = useCallback(() => {
    if (isCacheExpired) return;

    if (onComplete && (customerPreOrder || depositRequiredFromProps)) {
      // /book flow: go directly to payment — service charge is already in the basket
      // Allow proceeding when either customerPreOrder exists OR depositRequired exists
      if (isSubmitting || isScreenBusy) return;
      const result = buildCompleteResult();
      if (!result) return;
      setIsSubmitting(true);
      Promise.resolve(onComplete(result))
        .catch((err) => console.error("onComplete failed:", err))
        .finally(() => {
          setIsSubmitting(false);
        });
    } else {
      setState({ stage: "confirm" });
    }
  }, [
    isCacheExpired,
    onComplete,
    customerPreOrder,
    depositRequiredFromProps,
    buildCompleteResult,
    isSubmitting,
    isScreenBusy,
  ]);

  const showConfirmDetails = state.stage === "confirm";

  const hasLinkedBookings = linkedBookings != null && linkedBookings.length > 0;

  // For /book flow (onComplete provided): show service charge selector in the basket
  // Hide for linked bookings — service charge is not applicable when multiple booking types are chained.
  const shouldShowServiceChargeInBasket = shouldShowServiceChargeInBasketHelper(
    onComplete,
    customerPreOrder,
    needsServiceChargeConfirm,
    hasLinkedBookings,
  );

  // For /book flow: disable the confirm-stage service charge selector (already selectable in basket)
  // For /place-your-order flow (no onComplete): keep the confirm-stage selector as before
  // Hide for linked bookings — service charge is not applicable when multiple booking types are chained.
  const shouldShowServiceChargeSelector = shouldShowServiceChargeSelectorHelper(
    onComplete,
    showConfirmDetails,
    customerPreOrder,
    needsServiceChargeConfirm,
    hasLinkedBookings,
  );

  const currency = bookingData?.venue?.currency ?? "GBP";
  const countryCode = bookingData?.venue?.country_code ?? "GB";

  // The guest may only adjust the discretionary service charge when they
  // contribute to it.
  //
  // /book has no lead-booker/group-member distinction — the person booking is the
  // payer — so the charge is theirs to set whenever the pre-order requires payment.
  // A pay-at-venue pre-order is settled later at the venue's configured rate.
  //
  // /place-your-order is placed by a group member, so the charge is theirs when at
  // least one menu they ordered from is group-member paid. An order drawn only
  // from pay-at-venue or lead-booker-paid menus is not charged to them.
  const canChangeServiceCharge = onComplete
    ? (customerPreOrder?.requiredPaymentAmount ?? 0) > 0
    : isAnyOrderedMenuPaidByGroupMember(customerPreOrder);

  const preorderContextValue: PreorderContextValue | undefined = useMemo(() => {
    if (!bookingData) return undefined;
    return {
      bookingData,
      customerPreOrder,
      customerPreOrders: customerPreOrders ?? {},
      currency,
      countryCode,
      hidePricesByMenu,
      shouldHidePrices,
      forNames: effectiveForNames,
      orderingFor,
      multipleNames: effectiveMultipleNames,
      multiPersonOrderingEnabled,
      updateCustomerPreOrder: updateCustomerPreOrderAndClearExpiry,
      isItemPending,
      linkedBookings,
      bookingTypeName,
      bookingTypeId,
    };
  }, [
    bookingData,
    customerPreOrder,
    customerPreOrders,
    currency,
    countryCode,
    hidePricesByMenu,
    shouldHidePrices,
    effectiveForNames,
    orderingFor,
    effectiveMultipleNames,
    multiPersonOrderingEnabled,
    updateCustomerPreOrderAndClearExpiry,
    isItemPending,
    linkedBookings,
    bookingTypeName,
    bookingTypeId,
  ]);

  const wrappedHandleRemoveName = useCallback(
    async (index: number) => {
      try {
        await handleRemoveName(index);
      } catch (removeError) {
        console.error("Error in handleRemoveName:", removeError);
      }
    },
    [handleRemoveName],
  );

  // Guard: loading / error / unavailable states
  if (isLoadingPreorderData || !bookingData) {
    return (
      <PageStateGuard loading={isLoadingPreorderData} error={null} data={bookingData}>
        {null}
      </PageStateGuard>
    );
  }

  if (preorderLimitMessage) {
    return (
      <Layout
        showHeader={showHeader}
        main={
          <div className="app-main">
            <AlertBanner
              variant="warning"
              icon={<InfoCircle color="#9a3412" size={16} />}
              className="info-alert widget-error-panel"
            >
              {preorderLimitMessage}
            </AlertBanner>
            {onBack && (
              <div className="gcw:mt-bs-3">
                <PrimaryButton
                  text={t("back", "Back")}
                  type="button"
                  onClick={onBack}
                  className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:border gcw:border-primary gcw:bg-white gcw:text-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-gray-50 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
                  ariaLabel={t("back", "Back")}
                />
              </div>
            )}
          </div>
        }
        aside={null}
      />
    );
  }

  if (isPreorderUnavailable) {
    const refusalMessage = translatePreorderRefusal(t, preorderUnavailableMessage);
    return (
      <Layout
        showHeader={showHeader}
        main={
          <div className="app-main">
            <AlertBanner
              variant="warning"
              icon={<InfoCircle color="#9a3412" size={16} />}
              translatedKey="preorders_unavailable"
              messageDefault="Pre-orders are not available for this booking."
              className="info-alert widget-error-panel"
            >
              {refusalMessage}
            </AlertBanner>
            {onBack && (
              <div className="gcw:mt-bs-3">
                <PrimaryButton
                  text={t("back", "Back")}
                  type="button"
                  onClick={onBack}
                  className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:border gcw:border-primary gcw:bg-white gcw:text-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-gray-50 gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2"
                  ariaLabel={t("back", "Back")}
                />
              </div>
            )}
          </div>
        }
        aside={null}
      />
    );
  }

  if (!preorderContextValue) return null;

  const asideContent = (
    <AsideContent
      bookingId={bookingId}
      customerPreOrder={customerPreOrder}
      bookingData={bookingData}
      setForNames={setForNames}
      handleSetOrderingFor={handleSetOrderingFor}
      handleRemoveName={wrappedHandleRemoveName}
      handleNamesChanged={syncDietaryRequirementNames}
      showServiceChargeSelector={shouldShowServiceChargeSelector}
      onServiceChargeChange={handleServiceChargeChange}
      showServiceChargeInBasket={shouldShowServiceChargeInBasket}
      onBasketServiceChargeChange={handleServiceChargeChange}
      canChangeServiceCharge={canChangeServiceCharge}
      showConfirmDetails={showConfirmDetails}
      goToNextStep={goToNextStep}
      isOrderValid={isOrderValid}
      isSubmitting={isSubmitting || isScreenBusy}
      requiredPaymentAmount={customerPreOrder?.requiredPaymentAmount}
      menuLimits={menuLimits}
      onMaxItemsError={handleMaxItemsError}
      shouldHidePrices={shouldHidePrices}
      aggregatedTotals={aggregatedTotals}
      depositRequired={depositRequiredFromProps}
      onCacheExpired={handleCacheExpired}
      isCacheExpired={isCacheExpired}
      resyncKey={resyncKey}
      submitHidden={submitHidden}
    />
  );

  const hasBasketItems = (customerPreOrder?.items?.length ?? 0) > 0;
  const hasDepositOrItems = hasBasketItems || !!depositRequiredFromProps;
  const mobileAsideContent = isDesktop || hasDepositOrItems || isCacheExpired ? asideContent : null;

  // When portaling to the outer BookingForm aside, don't render aside in
  // the inner Layout — the portal renders it in the correct DOM location
  // while keeping React context (PreorderProvider) intact.
  const usePortal = !showHeader && !!basketPortalTarget;
  const layoutAside = usePortal ? null : mobileAsideContent;

  const preorderContent = (
    <PreorderContent
      editNames={editNames}
      addName={addName}
      addNameError={addNameError}
      namesExceedGuests={showNamesExceedGuests}
      onMultipleNamesToggle={handleMultipleNamesOptionChange}
      onAddName={handleAddName}
      onAddNameChange={setAddName}
      onValidationChange={handleValidationChange}
      onMenuLimitsChange={setMenuLimits}
      externalValidationErrors={externalValidationErrors}
      setCustomerPreOrder={setCustomerPreOrder}
      headingSlot={headingSlot}
    />
  );

  const mainContent = state.stage === "preorders" ? preorderContent : null;

  // In the /book flow (showHeader=false) during the preorders stage, skip the
  // inner Layout to avoid the unnecessary "no-sidebar app-layout" wrapper —
  // the outer BookingForm Layout already provides the page structure.
  if (!showHeader && state.stage === "preorders") {
    return (
      <PreorderProvider value={preorderContextValue}>
        {usePortal && createPortal(asideContent, basketPortalTarget)}
        {preorderContent}
      </PreorderProvider>
    );
  }

  return (
    <PreorderProvider value={preorderContextValue}>
      {usePortal && createPortal(asideContent, basketPortalTarget)}
      <Layout
        showHeader={showHeader}
        main={mainContent}
        mainClassName={showConfirmDetails && !isDesktop ? "confirm-fullscreen-mobile" : ""}
        asideClassName="basket-aside"
        aside={layoutAside}
        footer={null}
      />
    </PreorderProvider>
  );
};

export { PreorderExperience };
